// The canonical Studio control-plane implementation of `WeaponPersistence`,
// wired to sstk-studio-api's real Gunsmith endpoints (see
// contracts/studio-editor-studio-api.md's Agreed section):
//
//   GET  /projects/{id}/weapons                   -> summaries
//   GET  /projects/{id}/weapons/{weaponId}         -> {...summary, document}
//   PUT  /projects/{id}/weapons/{weaponId}         -> body: WeaponDocumentInput, saves unconditionally as a draft
//   POST /projects/{id}/weapons/{weaponId}/publish -> runs the publish-validation gate
//
// Deliberately calls `src/studio/services/api.ts`'s functions directly
// rather than going through `studioApiPersistence.ts`'s host-injectable
// `StudioApiTransport`: that indirection exists so map/character persistence
// can be reused by a real game host with its own auth once published via
// @sstk/build-kit's engine layer. Gunsmith is Studio-bespoke by design (see
// the plan's "New Studio editor surface" section) and never crosses that
// boundary, so there is no second host to abstract over here.
//
// Uses a dedicated `fetch` for the publish call specifically, rather than
// `apiPost`: `api.ts`'s `ApiHttpError` carries only `status`/`statusText`,
// not the parsed response body, so a 422's `{"detail":{"message","errors"}}`
// validation payload is unreachable through it - the one place this editor
// genuinely needs that body, since "which rule blocked publish" is real,
// actionable information for a creator, not just "publish failed".

import { getIdToken } from '../../services/auth';
import { API_URL, apiDelete, apiGet, apiPut } from '../../services/api';
import type { WeaponDocument } from '../../../engine/content/schema/weapon';
import {
  WeaponPublishValidationError,
  type WeaponPersistence,
  type WeaponPersistenceResult,
  type WeaponPublishResult,
} from './weaponPersistence';

interface WeaponSummaryWire {
  id: string;
  name: string;
  archetype: string;
  chassisId: string;
  published: boolean;
  publishedAt?: string | null;
  updatedAt?: string;
}

interface WeaponDetailWire extends WeaponSummaryWire {
  document: WeaponDocument;
}

const listPath = (projectId: string) => `/projects/${projectId}/weapons`;
const itemPath = (projectId: string, weaponId: string) => `/projects/${projectId}/weapons/${weaponId}`;
const publishPath = (projectId: string, weaponId: string) => `/projects/${projectId}/weapons/${weaponId}/publish`;

async function publishRequest(projectId: string, weaponId: string): Promise<WeaponPublishResult> {
  const token = await getIdToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const resp = await fetch(`${API_URL}${publishPath(projectId, weaponId)}`, { method: 'POST', headers });
  const text = await resp.text();
  const body: unknown = text ? JSON.parse(text) : undefined;
  if (!resp.ok) {
    if (typeof body === 'object' && body !== null && 'detail' in body) {
      const detail = (body as { detail: { message: string; errors?: string[] } }).detail;
      throw new WeaponPublishValidationError(detail.message, detail.errors ?? []);
    }
    throw new WeaponPublishValidationError(`Publish failed: ${resp.status} ${resp.statusText}`, []);
  }
  const success = body as { published: true; publishedAt: string };
  return { published: true, publishedAt: success.publishedAt };
}

export function createStudioApiWeaponPersistence(): WeaponPersistence {
  return {
    async listWeapons(projectId): Promise<WeaponPersistenceResult> {
      const summaries = (await apiGet<WeaponSummaryWire[] | null>(listPath(projectId))) ?? [];
      const details = await Promise.all(
        summaries.map((summary) => apiGet<WeaponDetailWire>(itemPath(projectId, summary.id))),
      );
      return {
        weapons: details.map((detail) => detail.document),
        publishedWeaponIds: summaries.filter((s) => s.published).map((s) => s.id),
      };
    },

    async saveWeapon(projectId, weapon: WeaponDocument): Promise<WeaponDocument> {
      await apiPut(itemPath(projectId, weapon.id), weapon);
      // Echoes back what was sent rather than re-fetching, matching
      // studioApiPersistence.ts's saveMap convention: the store's dirty-
      // check twin should reflect exactly what was written, not a second
      // guess at server state.
      return weapon;
    },

    async publishWeapon(projectId, weaponId): Promise<WeaponPublishResult> {
      return publishRequest(projectId, weaponId);
    },

    async deleteWeapon(projectId, weaponId): Promise<void> {
      // The server's 404s ("Project not found" / "Weapon not found") are
      // plain strings, not a structured multi-error validation payload like
      // publish's 422 - so this matches saveWeapon's shape (a plain apiDelete
      // call, generic ApiHttpError) rather than publishRequest's dedicated
      // fetch/body-parsing.
      await apiDelete(itemPath(projectId, weaponId));
    },
  };
}
